/**
 * Resizable detail panel layout.
 *
 * Renders a task list (children) on the left and the detail panel on the right,
 * separated by a draggable resize handle. The detail panel width is persisted
 * to localStorage and constrained between 320px and 60% of the container.
 */

import { useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import clsx from 'clsx';
import { X } from 'lucide-react';
import type { Task } from '../types';
import { TaskDetail } from './TaskDetail';

const STORAGE_KEY = 'danny:detail-panel-width';
const DEFAULT_WIDTH = 480;
const MIN_WIDTH = 380;
const MAX_WIDTH_PERCENT = 0.6;

/**
 * Mobile task-list scroller classes.
 *
 * pt: clears the fixed MobileTopBar (3.5rem icons + notch inset).
 * pb: reserves a buffer as tall as MobileBottomFade
 *     (calc(6rem + env(safe-area-inset-bottom))) so the LAST task can scroll
 *     clear of the gradient band the FAB/pill sit in — otherwise it stays
 *     trapped behind the opaque fade with no room to lift it into view.
 * md: no top bar and no bottom fade on desktop, so md:py-3 resets both insets
 *     to the compact value (responsive variant wins over the mobile arbitrary
 *     values at md+).
 *
 * Exported so DetailPanelLayout.test.tsx can assert the buffer without a DOM
 * (jsdom drops env()/max() from element.style — see PILL_BOTTOM_* precedent).
 */
export const MOBILE_LIST_SCROLLER_CLASS =
  'flex-1 overflow-y-auto px-4 pb-[calc(6rem+env(safe-area-inset-bottom,0px))] pt-[calc(3.5rem+env(safe-area-inset-top,0px))] md:py-3 md:pt-3 min-w-0';

interface DetailPanelLayoutProps {
  selectedTask: Task | null;
  onTaskClose: () => void;
  onTaskComplete: (taskId: string) => void;
  onTaskReopen?: (taskId: string) => void;
  onTaskUpdate: (task: Task) => void;
  onTaskSelectById: (taskId: string) => void;
  isPendingCompletion: boolean;
  children: ReactNode;
  /** When provided, renders this node in the right panel instead of the task detail */
  overridePanel?: ReactNode;
}

function getStoredWidth(): number {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v) {
      const n = parseInt(v, 10);
      if (n >= MIN_WIDTH) return n;
    }
  } catch { /* ignore */ }
  return DEFAULT_WIDTH;
}

export function DetailPanelLayout({
  selectedTask,
  onTaskClose,
  onTaskComplete,
  onTaskReopen,
  onTaskUpdate,
  onTaskSelectById,
  isPendingCompletion,
  children,
  overridePanel,
}: DetailPanelLayoutProps) {
  const showRightPanel = !!selectedTask || !!overridePanel;
  const [panelWidth, setPanelWidth] = useState(getStoredWidth);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const clampWidth = useCallback((w: number) => {
    const maxPx = containerRef.current
      ? containerRef.current.offsetWidth * MAX_WIDTH_PERCENT
      : 800;
    return Math.max(MIN_WIDTH, Math.min(w, maxPx));
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const startX = e.clientX;
    const startWidth = panelWidth;

    const onMove = (ev: PointerEvent) => {
      // Dragging left → detail gets wider (startX - ev.clientX is positive)
      const delta = startX - ev.clientX;
      const next = clampWidth(startWidth + delta);
      setPanelWidth(next);
    };

    const onUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      // persist
      setPanelWidth((cur) => {
        try { localStorage.setItem(STORAGE_KEY, String(cur)); } catch { /* ignore */ }
        return cur;
      });
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [panelWidth, clampWidth]);

  // Re-clamp on container resize
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      setPanelWidth((prev) => clampWidth(prev));
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [clampWidth]);

  return (
    <div ref={containerRef} className="flex-1 overflow-hidden flex min-h-0 relative">
      {/*
        Task list scroller — mobile top inset lives HERE, not on the
        Dashboard wrapper. Putting paddingTop on the wrapper instead
        kills scrolling because it double-dips with this value.
        3.5rem clears the MobileTopBar icons (~48px) with breathing room;
        env(safe-area-inset-top) adds notch clearance automatically.
        On desktop (md+) the responsive overrides reset to pt-3.
      */}
      <div
        data-testid="detail-panel-list-scroller"
        className={MOBILE_LIST_SCROLLER_CLASS}
      >
        {children}
      </div>

      {/* ── Mobile: half-screen bottom sheet ── */}
      {selectedTask && (
        <div className="md:hidden fixed inset-0 z-50">
          {/* Dimmed backdrop -- tap to close */}
          <div
            className="absolute inset-0 bg-black/40 transition-opacity"
            onClick={onTaskClose}
          />
          {/* Sheet -- slides up from bottom, max 65vh */}
          <div className="absolute bottom-0 inset-x-0 max-h-[65vh] bg-surface-raised-light dark:bg-surface-raised-dark rounded-t-2xl flex flex-col transform transition-transform duration-300 ease-out safe-area-bottom">
            {/* Drag handle */}
            <div className="flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 rounded-full bg-border-subtle-light dark:bg-border-subtle-dark" />
            </div>
            {/* Header: close button */}
            <div className="flex items-center justify-between px-4 pb-2 shrink-0">
              <button
                onClick={onTaskClose}
                className="p-2 -ml-1 rounded-lg hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors text-text-secondary-light dark:text-text-secondary-dark"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
              <span className="text-xs font-mono font-medium uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary-dark">
                Task Details
              </span>
              {/* Spacer to balance the close button */}
              <div className="w-9" />
            </div>
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              <TaskDetail
                task={selectedTask}
                onClose={onTaskClose}
                onComplete={onTaskComplete}
                onReopen={onTaskReopen}
                onTaskUpdate={onTaskUpdate}
                onSelectTask={onTaskSelectById}
                isPendingCompletion={isPendingCompletion}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Desktop (md+): existing resizable side panel ── */}

      {/* Drag handle */}
      {showRightPanel && (
        <div
          onPointerDown={handlePointerDown}
          className={clsx(
            'hidden md:flex items-center justify-center shrink-0',
            'w-1.5 cursor-col-resize group hover:bg-primary-500/20 active:bg-primary-500/30 transition-colors',
            'border-l border-border-subtle-light dark:border-border-subtle-dark',
          )}
        >
          <div className="w-0.5 h-8 rounded-full bg-border-subtle-light dark:bg-border-subtle-dark group-hover:bg-primary-500 transition-colors" />
        </div>
      )}

      {/* Task detail / override panel */}
      <div className={clsx(
        'hidden md:block overflow-hidden bg-surface-raised-light dark:bg-surface-base-dark',
        'relative flex-shrink-0',
        'transition-[width] duration-200 ease-out',
        showRightPanel ? '' : 'w-0 border-l-0'
      )}
        style={showRightPanel ? { width: undefined } : undefined}
      >
        <div
          className="w-full h-full overflow-y-auto"
          style={showRightPanel ? { width: `${panelWidth}px`, minWidth: `${MIN_WIDTH}px` } : undefined}
        >
          {overridePanel ?? (selectedTask && (
            <TaskDetail
              task={selectedTask}
              onClose={onTaskClose}
              onComplete={onTaskComplete}
              onReopen={onTaskReopen}
              onTaskUpdate={onTaskUpdate}
              onSelectTask={onTaskSelectById}
              isPendingCompletion={isPendingCompletion}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
